/**
 * Settings › Integrations (spec 027): how this workspace generates images.
 * Three choices: our image service (requests on the workspace's behalf),
 * the workspace's own OpenAI key, or off. Under the two on choices, the
 * monthly limit ("N of M used", resets on the 1st, UTC) and a field to
 * lower it. The page never receives or shows the key itself, and nothing
 * here says the service is free: the limit only protects the workspace.
 */
import React, { useEffect, useState } from 'react';
import type { ImageMode, ImageSettingsView } from '../../../Shared/src/images';
import { apiClient, type ImageSettingsUpdate } from '../api/client';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import { useImageGenStore } from '../stores/imageGenStore';
import styles from '../components/imagegen/ImageGen.module.scss';

export const SETTINGS_TEXT = {
  title: 'Integrations',
  section: 'Image generation',
  platform: 'StoryMotion image service',
  platformNote: "Requests use our OpenAI account on your workspace's behalf.",
  byok: 'Your own OpenAI key',
  byokNote: 'Requests are billed by OpenAI to your key.',
  off: 'Off',
  offNote: 'No Generate buttons. Copy each brief for your artist instead.',
  keyLabel: 'OpenAI API key',
  keyEnding: (last4: string) => `Key ending ••••${last4}`,
  replace: 'Replace',
  remove: 'Remove',
  used: (used: number, monthly: number) => `Monthly limit: ${used} of ${monthly} used`,
  resets: 'Resets on the 1st (UTC). Drafts count 1, a full-size picture 10, describing your pictures 1.',
  lower: 'Lower the limit',
  useServer: (m: number) => `Use the server limit (${m})`,
  save: 'Save',
  saved: 'Saved',
  rejected: 'That key was rejected by OpenAI.',
  limitInvalid: (m: number) => `Choose a limit from 1 to ${m}.`,
  missing: 'Enter a key to use your own OpenAI account.',
  unavailable: 'This server cannot store keys yet (no IMAGE_KEY_SECRET).',
  invalidKey: 'An OpenAI key starts with sk-.',
  notSetUp: 'The StoryMotion image service is not set up on this server.',
} as const;

const errorText = (error: string, s: ImageSettingsView | null): string => {
  switch (error) {
    case 'byok_rejected': return SETTINGS_TEXT.rejected;
    case 'limit_invalid': return SETTINGS_TEXT.limitInvalid(s?.limit.serverMonthly ?? 0);
    case 'byok_missing': return SETTINGS_TEXT.missing;
    case 'byok_unavailable': return SETTINGS_TEXT.unavailable;
    case 'byok_invalid': return SETTINGS_TEXT.invalidKey;
    default: return error;
  }
};

export const IntegrationsSettings: React.FC = () => {
  const [settings, setSettings] = useState<ImageSettingsView | null>(null);
  const [mode, setMode] = useState<ImageMode>('off');
  const [key, setKey] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [removeKey, setRemoveKey] = useState(false);
  const [limit, setLimit] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const adopt = (s: ImageSettingsView) => {
    setSettings(s);
    setMode(s.mode);
    setLimit(s.limit.own ? String(s.limit.own) : '');
    setKey('');
    setReplacing(false);
    setRemoveKey(false);
  };

  useEffect(() => {
    void apiClient.getImageSettings().then((res) => {
      if (res.success) adopt(res.data);
      else setError(res.error);
    }).catch(() => setError('Could not reach the server.'));
  }, []);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    const body: ImageSettingsUpdate = { mode };
    if (mode === 'byok' && key.trim()) body.byokKey = key.trim();
    else if (removeKey) body.byokKey = '';
    if (mode !== 'off') {
      const n = limit.trim() === '' ? 0 : Number(limit);
      if (n !== (settings.limit.own ?? 0)) body.monthlyLimit = Number.isFinite(n) ? n : -1;
    }
    try {
      const res = await apiClient.saveImageSettings(body);
      if (res.success) {
        adopt(res.data);
        setSaved(true);
        void useImageGenStore.getState().loadSettings();
      } else {
        setError(errorText(res.error, settings));
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return (
      <div className={styles.settings} data-testid="settings-integrations">
        <h2>{SETTINGS_TEXT.title}</h2>
        {error ? <p className={styles.error}>{error}</p> : <p className={styles.muted}>Loading…</p>}
      </div>
    );
  }

  const keySet = settings.byok.set && !removeKey;
  const usedLine = (
    <span className={styles.muted} data-testid="settings-used">{SETTINGS_TEXT.used(settings.limit.used, settings.limit.monthly)}</span>
  );
  const limitField = (
    <div className={styles.field} data-testid="settings-limit">
      <span className={styles.muted}>{SETTINGS_TEXT.resets}</span>
      <label className={styles.field}>
        {SETTINGS_TEXT.lower}
        <input
          type="number"
          min={1}
          max={settings.limit.serverMonthly}
          step={1}
          value={limit}
          placeholder={String(settings.limit.serverMonthly)}
          onChange={(e) => setLimit(e.target.value)}
          data-testid="settings-limit-input"
        />
      </label>
      {limit !== '' && (
        <button type="button" className={styles.link} onClick={() => setLimit('')} data-testid="settings-limit-clear">
          {SETTINGS_TEXT.useServer(settings.limit.serverMonthly)}
        </button>
      )}
    </div>
  );

  const choice = (value: ImageMode, title: string, note: string, always?: React.ReactNode, selected?: React.ReactNode) => (
    <div className={`${styles.choice} ${mode === value ? styles.choiceOn : ''}`} data-testid={`settings-choice-${value}`}>
      <label>
        <input type="radio" name="image-mode" checked={mode === value} onChange={() => setMode(value)} data-testid={`settings-mode-${value}`} />
        {title}
      </label>
      <span className={styles.note}>{note}</span>
      {always}
      {mode === value && selected}
    </div>
  );

  return (
    <div className={styles.settings} data-testid="settings-integrations">
      <h2>{SETTINGS_TEXT.title}</h2>
      <h3>{SETTINGS_TEXT.section}</h3>
      {choice('platform', SETTINGS_TEXT.platform, SETTINGS_TEXT.platformNote, (
        <>
          {!settings.available && settings.mode === 'platform' && <span className={styles.muted}>{SETTINGS_TEXT.notSetUp}</span>}
          {usedLine}
        </>
      ), limitField)}
      {choice('byok', SETTINGS_TEXT.byok, SETTINGS_TEXT.byokNote, (
        <>
          {keySet && !replacing ? (
            <div className={styles.row}>
              <span data-testid="settings-key-ending">{SETTINGS_TEXT.keyEnding(settings.byok.last4 ?? '')}</span>
              <button type="button" className={styles.button} onClick={() => setReplacing(true)} data-testid="settings-key-replace">{SETTINGS_TEXT.replace}</button>
              <button type="button" className={styles.button} onClick={() => { setRemoveKey(true); setKey(''); }} data-testid="settings-key-remove">{SETTINGS_TEXT.remove}</button>
            </div>
          ) : (
            <label className={styles.field}>
              {SETTINGS_TEXT.keyLabel}
              <input
                type="password"
                autoComplete="off"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="sk-…"
                data-testid="settings-key-input"
              />
            </label>
          )}
          {usedLine}
        </>
      ), limitField)}
      {choice('off', SETTINGS_TEXT.off, SETTINGS_TEXT.offNote)}
      {error && <p className={styles.error} role="alert" data-testid="settings-error">{error}</p>}
      {saved && !error && <p className={styles.status} role="status">{SETTINGS_TEXT.saved}</p>}
      <div>
        <button type="button" className={styles.primary} disabled={busy} onClick={() => { void save(); }} data-testid="settings-save">
          {SETTINGS_TEXT.save}
        </button>
      </div>
    </div>
  );
};

const Settings: React.FC = () => (
  <Layout subtitle="Settings" sidebar={<Sidebar />}>
    <IntegrationsSettings />
  </Layout>
);

export default Settings;
